import SearchIcon from "@mui/icons-material/SearchOutlined";
import { Tooltip } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { ReactComponent as DownloadIcon } from "../../assets/images/download-icon.svg";
import { ReactComponent as PercentIcon } from "../../assets/images/percentage-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import { ReactComponent as ToggleBarIcon } from "../../assets/images/toggle-arrow.svg";
import { ReactComponent as ViewIcon } from "../../assets/images/view-icon.svg";
import { ReactComponent as ReplayIcon } from "../../assets/images/replay-icon.svg";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import CRadioGroup from "../../components/CRadioGroup";
import CSelect from "../../components/CSelect";
import CTextField from "../../components/CTextField";
import { supabase } from "../../config/supabaseClient";
import { messages } from "../../constant/alertMessages";
import {
  allocationType,
  statusType,
  jobType,
  generateTrainingPageBatchJobTypes,
  statuses,
  roleType,
  SYSTEM_APPROACHES,
  SKILLS_PLANNER_FORM_STATUS_FILTER,
  SKILLS_PLANNER_FORM_STATUS_FILTER_OPTIONS,
  resolveSkillsPlannerFormStatusFilter,
  getTrainingFormsRpcParams,
} from "../../constant/constants";
import {
  capitalizeFirstCharacter,
  formatZAR,
  getLocalData,
  ROWS_PER_PAGE_OPTIONS,
} from "../../constant/utils/commonUtils";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import {store} from "../../store";
import {
  addHistoryData,
  addHistoryTableId,
  addPrograms,
  addSummary,
  allocatePrograms,
  deleteHistoryData,
  employeeStatistics,
  trainingHistory,
} from "./../../slices/trainingProgrammeSlice";
import AddIcon from "@mui/icons-material/Add";
import CDialog from "../../components/CDialog";
import CloseIcon from "@mui/icons-material/Close";
import { fetchYearId } from "../../slices/yearSlice";
import { fetchConfiguration } from "../../slices/configSlice";
import { fetchTotalBudget } from "../../slices/totalBudgetSlice";
import {
  collectPlanEmployeeDataIssues,
  formatEmployeeDataIssueText,
} from "../../assets/common/commonService";
import {
  SKILLS_PLANNER_HELPER_PROMPT,
  buildSkillsPlanInputFormatSpec,
  buildSkillsPlanOutputFormatSpec,
  employmentTypesFromSetaFields,
  expectedParserRowCountFromFormated,
  extractParserSpec,
  formatedDataRecordFromRpcRow,
  mergeParserOutputWithAllocationDefaults,
  normalizeSchemasForParserHelper,
  postSkillsPlannerHelper,
  runParserOnRecords,
} from "../../utils/skillsPlannerUtils";

const EMPLOYEE_DATA_WARNING_LIST_LIMIT = 10;

const EMPTY_EMPLOYEE_DATA_WARNING_DIALOG = {
  show: false,
  issues: [],
  pendingTrainingFormData: null,
  filterLabel: "",
};

function getSkillsPlannerBatchSize() {
  const raw = process.env.REACT_APP_SKILLS_PLANNER_BATCH_SIZE;
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return 200;
  }
  const n = parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return 200;
  return n;
}

const SKILLS_PLANNER_MAX_RETRIES = 3;

function delaySkillsPlannerRetry(attemptIndex) {
  return new Promise((resolve) =>
    setTimeout(resolve, Math.min(2500, 400 * attemptIndex))
  );
}

function getGetTrainingFormsClientBatchSize() {
  const raw = process.env.REACT_APP_GET_TRAINING_FORMS_BATCH_SIZE;
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return null;
  }
  const n = parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

async function fetchAllTrainingFormsForGeneratePlan(
  supabaseClient,
  companyId,
  formStatusFilter
) {
  const rpcFilterParams = getTrainingFormsRpcParams(formStatusFilter);
  const pageSize = getGetTrainingFormsClientBatchSize();
  if (pageSize == null) {
    const { data, error } = await supabaseClient.rpc("get_training_forms", {
      p_company_id: companyId,
      ...rpcFilterParams,
    });
    return {
      data: Array.isArray(data) ? data : [],
      error,
    };
  }

  const all = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabaseClient.rpc("get_training_forms", {
      p_company_id: companyId,
      ...rpcFilterParams,
      p_batch_size: pageSize,
      p_processed_record: offset,
    });
    if (error) {
      return { data: all, error };
    }
    const chunk = Array.isArray(data) ? data : [];
    all.push(...chunk);
    if (chunk.length < pageSize) {
      break;
    }
    offset += chunk.length;
  }
  return { data: all, error: null };
}

function computeSkillsPlannerProgressUnits(
  nEmployees,
  expectedAllocationRows,
  batchSize
) {
  const bs = batchSize > 0 ? batchSize : 200;
  const insertPhases = Math.max(1, Math.ceil(expectedAllocationRows / bs));
  const helperWeight = Math.max(
    3,
    Math.min(30, Math.round(nEmployees * 0.12))
  );
  const mergeWeight = 1;
  return {
    helperWeight,
    mergeWeight,
    insertPhases,
  };
}

const GenerateTraining = () => {
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(true);
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [budget, setBudget] = useState({
    totalBudget: null,
    allocateBasedOn: "",
    remainingBudget: 100,
  });

  const localData = getLocalData("sdfUser");
  const [raceBudget, setRaceBudget] = useState({});
  const [genderBudget, setGenderBudget] = useState({});
  const [trainingCostFieldName, setTrainingCostFieldName] = useState("");
  const [dialogState, setDialogState] = useState({ show: false });
  const [isLoading, setIsLoading] = useState(false);

  const [formStatusFilter, setFormStatusFilter] = useState(
    SKILLS_PLANNER_FORM_STATUS_FILTER.ALL,
  );
  const [allowCreateNew, setAllowCreateNew] = useState(true);
  const [resumingId, setResumingId] = useState(null);
  const [generatePlanLoading, setGeneratePlanLoading] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [employeeDataWarningDialog, setEmployeeDataWarningDialog] = useState(
    EMPTY_EMPLOYEE_DATA_WARNING_DIALOG,
  );
  const {
    statistics,
    setaTemplate,
    employeeTemplate,
    employmentTypes,
    fileStructure,
    history,
    historyTableId,
    summary,
    allocatedPrograms,
    employeeData,
    configData,
  } = useSelector(
    (store) => ({
      statistics: store.trainingProgramme.statistics,
      allocatedPrograms: store.trainingProgramme.allocatedPrograms,
      setaTemplate: store.setaTemplate.setaTemplate,
      employeeTemplate: store.setaTemplate.employeeTemplate,
      employmentTypes: store.setaTemplate.employmentTypes,
      fileStructure: store.setaTemplate.fileStructure,
      history: store.trainingProgramme.history,
      historyTableId: store.trainingProgramme.historyTableId,
      summary: store.trainingProgramme.summary,
      employeeData: store.employee.employee,
      configData: store.config.configData,
    }),
    shallowEqual
  );
  
  const { totalBudget } = useSelector((state) => state.totalBudget);
  const [columns, setColumns] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
  const companyId = getLocalData("sdfUser")?.company_id;

  dispatch(fetchYearId()).then((res) => {
    if (!res.error) {
      const yearId = res.payload;

      dispatch(fetchTotalBudget({ yearId, companyId }));

      if (!configData) {
        dispatch(fetchConfiguration({ yearId }));
      }
    } else {
      toast.error(res.error);
    }
  });

  if (!setaTemplate.length) {
    dispatch(fetchSetaTemplateByCompanyId(companyId));
  }

    dispatch(trainingHistory(companyId));
  }, []);

  useEffect(() => {
    if (!configData) return;
    if (!configData.training_need_workflow) {
      dispatch(employeeStatistics(getLocalData("sdfUser")?.company_id)).then(
        (res) => {
          if (!res.payload.error) {
            let raceFields = {};
            Object.keys(res.payload?.data?.race).forEach(
              (ele) => (raceFields = { ...raceFields, [ele]: null })
            );

            let genderFields = {};
            Object.keys(res.payload?.data?.gender).forEach(
              (ele) => (genderFields = { ...genderFields, [ele]: null })
            );
            setRaceBudget(raceFields);
            setGenderBudget(genderFields);
          }
        }
      );
    }
  }, [configData]);

  useEffect(() => {
    if (!history.length) return;
    const latestHistory = history[0];
    const isEdgeTrainingPlanProcessing =
      latestHistory.status === statusType.PROCESSING &&
      latestHistory.job_type === jobType.TRAINING;
    const isSkillsPlannerProcessing =
      latestHistory.status === statusType.PROCESSING &&
      latestHistory.job_type === jobType.SKILLS_PLANNER_ALLOCATION;

    if (isEdgeTrainingPlanProcessing) {
      if (!historyTableId || historyTableId !== latestHistory.id) {
        dispatch(addHistoryTableId(latestHistory.id));
      }
      !allocatedPrograms.length && setIsLoading(true);
      setAllowCreateNew(false);
    } else if (!isSkillsPlannerProcessing) {
      setAllowCreateNew(true);
      setIsLoading(false);
    }
  }, [history, historyTableId, allocatedPrograms.length]);

  useEffect(() => {
    if (!setaTemplate.length) return;

    const isCellBlank = (v) =>
      v === undefined ||
      v === null ||
      v === "" ||
      (typeof v === "string" && v.trim() === "");

    let newColumns = [];
    setaTemplate.forEach((field, index) => {
      if (field?.config?.isTrainingCostField) {
        setTrainingCostFieldName(field.fieldName);
      }
      if (field.config.generatePlan) {
        const colId = `plan_${index}`;
        newColumns.push({
          headerName: field.fieldName,
          field: colId,
          minWidth: field.fieldName.length > 10 ? 200 : 100,
          id: colId,
          renderCell: ({ row }) => {
            const value = field.config.isAdditionalField
              ? (row.allocated_additional_field?.[field.fieldName] ?? row[field.fieldName])
              : row[field.fieldName];
            const fieldTypeLc = String(field.type || "").toLowerCase();
            const isDateTypeField =
              fieldTypeLc === "date" ||
              fieldTypeLc === "datetime" ||
              fieldTypeLc === "datepicker";
            if (field.config.isTrainingCostField) {
              const num = Number(value);
              return (
                <p>
                  {!isCellBlank(value) && !Number.isNaN(num) ? formatZAR(num) : "-"}
                </p>
              );
            }
            if (isDateTypeField) {
              if (isCellBlank(value)) return <p>-</p>;
              const m = moment(value);
              return (
                <p>{m.isValid() ? m.format("DD/MM/YYYY") : String(value)}</p>
              );
            }
            if (field.type === "number") {
              if (!isCellBlank(value)) {
                const num = Number(value);
                if (!Number.isNaN(num)) return <p>{num}</p>;
              }
            }
            if (Array.isArray(value)) {
              return value.length ? value.join(", ") : "-";
            }
            if (isCellBlank(value)) return <p>-</p>;
            return <p>{String(value)}</p>;
          },
        });
      }
    });
    const additionalFieldColumns = (setaTemplate || [])
      .filter((f) => f?.config?.isAdditionalField && !f?.config?.generatePlan)
      .map((field, idx) => {
        const colId = `additional_${idx}`;
        const isNum = field.type === "number" || field?.config?.isTrainingCostField;
        return {
          headerName: field.fieldName,
          field: colId,
          minWidth: field.fieldName.length > 10 ? 200 : 100,
          id: colId,
          renderCell: ({ row }) => {
            const raw = row.allocated_additional_field?.[field.fieldName] ?? row[field.fieldName];
            const fieldTypeLc = String(field.type || "").toLowerCase();
            const isDateTypeField =
              fieldTypeLc === "date" ||
              fieldTypeLc === "datetime" ||
              fieldTypeLc === "datepicker";
            if (isNum && (raw === 0 || raw === "0")) return <p>{Number(raw)}</p>;
            if (isNum && !isCellBlank(raw)) {
              const num = Number(raw);
              if (!Number.isNaN(num))
                return (
                  <p>{field?.config?.isTrainingCostField ? formatZAR(num) : num}</p>
                );
            }
            if (isCellBlank(raw)) return <p>-</p>;
            if (isDateTypeField) {
              const m = moment(raw);
              return (
                <p>{m.isValid() ? m.format("DD/MM/YYYY") : String(raw)}</p>
              );
            }
            if (Array.isArray(raw)) {
              return raw.length ? raw.join(", ") : "-";
            }
            return <p>{String(raw)}</p>;
          },
        };
      });
    setColumns([...newColumns, ...additionalFieldColumns]);
  }, [setaTemplate]);

  const handleBudgetChange = async (e) => {
    if (isNaN(e.target.value)) return;
    const newValue = e.target.value || 0;

    const currentBudget =
      budget.allocateBasedOn === allocationType.GENDER
        ? genderBudget
        : raceBudget;

    let totalExcludingCurrent = 0;
    Object.keys(currentBudget).forEach((key) => {
      if (key !== e.target.name) {
        totalExcludingCurrent += Number(currentBudget[key]) || 0;
      }
    });
    const remainingForField = Number((100 - totalExcludingCurrent).toFixed(2));
    const updatedTotal = (totalExcludingCurrent + Number(newValue)).toFixed(2);
    const updatedRemaining = (100 - updatedTotal).toFixed(2);
    if (Number(newValue) > 100 - totalExcludingCurrent) {
      toast.error(`${messages.MAXIMUM_ALLOWED_VALUE}: ${remainingForField}`);

      if (budget.allocateBasedOn === allocationType.GENDER) {
        setGenderBudget({
          ...genderBudget,
          [e.target.name]: remainingForField,
        });
      } else if (budget.allocateBasedOn === allocationType.RACE) {
        setRaceBudget({
          ...raceBudget,
          [e.target.name]: remainingForField,
        });
      }
      setBudget({ ...budget, remainingBudget: 0 });
      return;
    }

    setBudget((prev) => ({
      ...prev,
      remainingBudget: updatedRemaining,
    }));

    if (budget.allocateBasedOn === allocationType.GENDER) {
      setGenderBudget((prev) => ({
        ...prev,
        [e.target.name]: newValue || null,
      }));
    } else if (budget.allocateBasedOn === allocationType.RACE) {
      setRaceBudget((prev) => ({
        ...prev,
        [e.target.name]: newValue || null,
      }));
    }
  };

  const findTotal = () => {
    let keys =
      budget.allocateBasedOn === allocationType.GENDER
        ? Object.keys(genderBudget)
        : Object.keys(raceBudget);

    let total = 0;
    for (let key of keys) {
      let currentValue =
        budget.allocateBasedOn === allocationType.GENDER
          ? Number(genderBudget[key]) || 0
          : Number(raceBudget[key]) || 0;
      total += currentValue;
    }
    return total;
  };

  const runSkillsPlannerGeneratePlan = async (trainingFormData) => {
    if (!localData?.company_id) return;
    if (!Array.isArray(trainingFormData) || trainingFormData.length === 0) {
      toast.error("No training forms for this company/filter.");
      return;
    }

    setIsGeneratingPlan(true);
    setGeneratePlanLoading(true);
    let batchJobId = null;

    const patchBatchJob = async (id, patch, logStep) => {
      const { data, error } = await supabase
        .from("batch_jobs")
        .update(patch)
        .eq("id", id)
        .select("*")
        .maybeSingle();
      if (error) {
        console.error("[Skills Plan] batch_jobs PATCH failed", logStep || "", error);
      } else if (data) {
        dispatch(addHistoryData(data));
      }
      return { data, error };
    };

    const markBatchJobFailed = async (id) => {
      if (!id) return;
      const { data, error } = await supabase
        .from("batch_jobs")
        .update({ status: statusType.FAILED })
        .eq("id", id)
        .select("*")
        .maybeSingle();
      if (error) console.error("[Skills Plan] batch_jobs mark failed", error);
      if (data) dispatch(addHistoryData(data));
    };

    try {
      const fields = Array.isArray(setaTemplate) ? setaTemplate : [];
      if (fields.length === 0) {
        toast.error("SETA template is not loaded.");
        return;
      }

      const empTypesFromField = employmentTypesFromSetaFields(fields);
      const empTypesForRpc =
        empTypesFromField.length > 0
          ? empTypesFromField
          : Array.isArray(employmentTypes)
            ? employmentTypes
            : [];

      const inputDataFormat = buildSkillsPlanInputFormatSpec(fields);
      const outputDataFormat = buildSkillsPlanOutputFormatSpec(fields);
      const { inputSchema, outputSchema } = normalizeSchemasForParserHelper(
        inputDataFormat,
        outputDataFormat
      );

      const formatedRecords = trainingFormData
        .map((row) =>
          formatedDataRecordFromRpcRow(
            row,
            empTypesForRpc,
            fields,
            employeeTemplate
          )
        )
        .filter(Boolean);
      if (formatedRecords.length === 0) {
        toast.error("Could not build formatedData from training forms.");
        return;
      }

      const expectedAllocRows =
        expectedParserRowCountFromFormated(formatedRecords);
      if (expectedAllocRows === 0) {
        toast.error("No allocated trainings in the selected records.");
        return;
      }

      const nEmp = formatedRecords.length;
      const plannerBatchSize = getSkillsPlannerBatchSize();
      let progressUnits = computeSkillsPlannerProgressUnits(
        nEmp,
        expectedAllocRows,
        plannerBatchSize
      );

      let yearId = null;
      try {
        yearId = await store.dispatch(fetchYearId()).unwrap();
      } catch {}

      const preliminaryInputData = {
        totalBudget: totalBudget ?? 0,
        usedBudget: 0,
        form_status_filter: formStatusFilter,
        approved_only:
          formStatusFilter === SKILLS_PLANNER_FORM_STATUS_FILTER.APPROVED,
        allocateBasedOn: budget.allocateBasedOn || allocationType.EQUAL,
        ...(budget.allocateBasedOn === allocationType.GENDER && {
          gender: genderBudget,
        }),
        ...(budget.allocateBasedOn === allocationType.RACE && {
          race: raceBudget,
        }),
        source: "skills_planner_generate_plan",
        skills_planner_units: {
          n: nEmp,
          helper_weight: progressUnits.helperWeight,
          merge_weight: progressUnits.mergeWeight,
          insert_phases: progressUnits.insertPhases,
          expected_allocation_rows: expectedAllocRows,
        },
      };

      const { data: historyJob, error: jobInsErr } = await supabase
        .from("batch_jobs")
        .insert({
          company_id: localData.company_id,
          created_by: localData.id,
          input_data: preliminaryInputData,
          status: statusType.PROCESSING,
          total_records: expectedAllocRows,
          processed_records: 0,
          job_type: jobType.SKILLS_PLANNER_ALLOCATION,
          year_id: yearId,
        })
        .select("*")
        .single();

      if (jobInsErr || !historyJob?.id) {
        console.error("[Skills Plan] batch_jobs insert", jobInsErr);
        toast.error(jobInsErr?.message || messages.SERVER_ERROR);
        return;
      }

      batchJobId = historyJob.id;
      dispatch(addHistoryData(historyJob));

      const expectedRows = expectedAllocRows;
      const nRec = formatedRecords.length;
      const parseChunks = Math.max(1, Math.ceil(nRec / plannerBatchSize));

      const tryFetchParserSpec = async () => {
        let lastContent = null;
        let lastExtractedSpec = null;
        for (let attempt = 1; attempt <= SKILLS_PLANNER_MAX_RETRIES; attempt++) {
          try {
            const content = await postSkillsPlannerHelper(
              SKILLS_PLANNER_HELPER_PROMPT,
              {
                inputSchema,
                outputSchema,
              }
            );
            const extracted = extractParserSpec(content);
            if (extracted?.functionBody) {
              return { spec: extracted };
            }
            lastContent = content;
            lastExtractedSpec = extracted;
          } catch {}
          if (attempt < SKILLS_PLANNER_MAX_RETRIES) {
            await delaySkillsPlannerRetry(attempt);
          }
        }
        console.error(
          "[Skills Plan] Generate Plan failed: no functionBody from AI /helper after retries",
          {
            batch_jobs_id: batchJobId,
            contentType: typeof lastContent,
            contentLength:
              typeof lastContent === "string"
                ? lastContent.length
                : lastContent != null
                  ? JSON.stringify(lastContent).length
                  : 0,
            contentPreview:
              typeof lastContent === "string"
                ? lastContent.slice(0, 1200)
                : lastContent,
            extractedSpec: lastExtractedSpec,
            hint: "extractParserSpec could not find functionBody; see contentPreview or backend /helper logs.",
          }
        );
        return null;
      };

      const runAllParseChunks = async (spec) => {
        const rows = [];
        for (let i = 0; i < nRec; i += plannerBatchSize) {
          const slice = formatedRecords.slice(i, i + plannerBatchSize);
          const parseChunkIdx = Math.floor(i / plannerBatchSize) + 1;
          let part = null;
          let lastParseErr = null;
          for (let attempt = 1; attempt <= SKILLS_PLANNER_MAX_RETRIES; attempt++) {
            try {
              part = runParserOnRecords(spec.functionBody, slice);
              lastParseErr = null;
              break;
            } catch (parseErr) {
              lastParseErr = parseErr;
            }
            if (attempt < SKILLS_PLANNER_MAX_RETRIES) {
              await delaySkillsPlannerRetry(attempt);
            }
          }
          if (lastParseErr != null) {
            const err =
              lastParseErr instanceof Error
                ? lastParseErr
                : new Error(
                    typeof lastParseErr === "string"
                      ? lastParseErr
                      : JSON.stringify(lastParseErr)
                  );
            const chunkLabel = `(chunk ${parseChunkIdx}/${parseChunks}, employees ${i + 1}–${i + slice.length} of ${nRec})`;
            console.error(
              "[Skills Plan] Generate Plan failed: parser threw after retries",
              chunkLabel,
              {
                technicalMessage: err.message,
                name: err.name,
                stack: err.stack,
                cause: err.cause,
                batch_jobs_id: batchJobId,
                sliceStartIndex: i,
                sliceEmployeeCount: slice.length,
                sampleEmpIds: slice.slice(0, 8).map((r) => r?.emp_id),
                retriesUsed: SKILLS_PLANNER_MAX_RETRIES,
                functionBodyLength:
                  typeof spec.functionBody === "string"
                    ? spec.functionBody.length
                    : null,
                functionBodyHead:
                  typeof spec.functionBody === "string"
                    ? spec.functionBody.slice(0, 400)
                    : null,
                functionBodyTail:
                  typeof spec.functionBody === "string" &&
                  spec.functionBody.length > 400
                    ? spec.functionBody.slice(-200)
                    : null,
              }
            );
            console.error("[Skills Plan] parser — raw thrown value", lastParseErr);
            toast.error(
              messages.SKILLS_PLAN_PARSER_FAILED(chunkLabel, err.message)
            );
            throw err;
          }
          rows.push(...part);
        }
        return rows;
      };

      const firstSpecResult = await tryFetchParserSpec();
      if (!firstSpecResult) {
        toast.error(messages.SKILLS_PLAN_MISSING_FUNCTION_BODY);
        await markBatchJobFailed(batchJobId);
        return;
      }
      let spec = firstSpecResult.spec;

      let transformedRows;
      try {
        transformedRows = await runAllParseChunks(spec);
      } catch {
        await markBatchJobFailed(batchJobId);
        return;
      }

      if (transformedRows.length !== expectedRows) {
        let resolved = false;
        for (
          let repass = 2;
          repass <= SKILLS_PLANNER_MAX_RETRIES;
          repass++
        ) {
          const again = await tryFetchParserSpec();
          if (!again) {
            console.error(
              "[Skills Plan] row mismatch retry aborted: helper did not return functionBody"
            );
            break;
          }
          spec = again.spec;
          try {
            transformedRows = await runAllParseChunks(spec);
          } catch {
            await markBatchJobFailed(batchJobId);
            return;
          }
          if (transformedRows.length === expectedRows) {
            resolved = true;
            break;
          }
        }
        if (!resolved) {
          console.error(
            "[Skills Plan] Generate Plan failed: parser output row count does not match training allocations (after retries)",
            {
              batch_jobs_id: batchJobId,
              employees: nRec,
              expectedRows,
              actualRows: transformedRows.length,
              difference: transformedRows.length - expectedRows,
              hint: "The AI must return one output row per trainingsAllocated[] entry (order preserved). Check transformMode oneRowPerTrainingItem and template fields.",
            }
          );
          toast.error(
            messages.SKILLS_PLAN_ROW_COUNT_MISMATCH(
              transformedRows.length,
              expectedRows
            )
          );
          await markBatchJobFailed(batchJobId);
          return;
        }
      }

      const costField =
        trainingCostFieldName ||
        setaTemplate?.find((f) => f?.config?.isTrainingCostField)?.fieldName ||
        "";
      const { rows: mergedRows, flattenedCosts } =
        mergeParserOutputWithAllocationDefaults(
          transformedRows,
          formatedRecords,
          costField,
          fields
        );

      const insertPhasesActual = Math.max(
        1,
        Math.ceil(mergedRows.length / plannerBatchSize)
      );
      if (insertPhasesActual !== progressUnits.insertPhases) {
        progressUnits = {
          ...progressUnits,
          insertPhases: insertPhasesActual,
        };
      }

      const saveRowTotal = mergedRows.length;
      if (saveRowTotal !== expectedAllocRows) {
        await patchBatchJob(
          batchJobId,
          {
            total_records: saveRowTotal,
            input_data: {
              ...preliminaryInputData,
              skills_planner_units: {
                ...preliminaryInputData.skills_planner_units,
                insert_phases: insertPhasesActual,
                merged_allocation_rows: saveRowTotal,
              },
            },
          },
          "Adjusted batch_jobs.total_records to merged row count (before inserts)"
        );
      }

      const usedBudget = mergedRows.reduce((sum, _, i) => {
        const c = flattenedCosts[i];
        return sum + (c != null && c !== "" && !Number.isNaN(Number(c))
          ? Number(c)
          : 0);
      }, 0);

      const skillsPlannerInputDataBase = {
        ...preliminaryInputData,
        skills_planner_units: {
          ...preliminaryInputData.skills_planner_units,
          insert_phases: progressUnits.insertPhases,
          saved_allocation_rows: mergedRows.length,
        },
      };

      const input_data = {
        ...skillsPlannerInputDataBase,
        usedBudget,
      };

      const rowsToInsert = mergedRows.map((item) => ({
        data: item,
        batch_jobs_id: batchJobId,
      }));
      const allocatedData = [];
      let rowsPersisted = 0;
      const insertChunks = Math.max(
        1,
        Math.ceil(rowsToInsert.length / plannerBatchSize)
      );
      for (let i = 0; i < rowsToInsert.length; i += plannerBatchSize) {
        const chunk = rowsToInsert.slice(i, i + plannerBatchSize);
        const insertChunkIdx = Math.floor(i / plannerBatchSize) + 1;
        const { data: part, error: allocatedError } = await supabase
          .from("allocated_programmes")
          .insert(chunk)
          .select("data, id");
        if (allocatedError) {
          console.error("[Skills Plan] allocated_programmes insert failed", allocatedError);
          toast.error(allocatedError.message || messages.SERVER_ERROR);
          await supabase
            .from("allocated_programmes")
            .delete()
            .eq("batch_jobs_id", batchJobId);
          await supabase.from("batch_jobs").delete().eq("id", batchJobId);
          batchJobId = null;
          return;
        }
        const insertedCount = part?.length ?? 0;
        if (part?.length) allocatedData.push(...part);
        rowsPersisted += insertedCount;

        const rowDataSoFar = allocatedData.map((ele) => ({
          ...ele.data,
          uniqueId: ele.id,
        }));
        const sumPayload = findSummary(rowDataSoFar, {
          input_data: preliminaryInputData,
        });
        const progressInputData = {
          ...skillsPlannerInputDataBase,
          usedBudget: sumPayload.budgetUsed,
        };

        await patchBatchJob(
          batchJobId,
          {
            processed_records: rowsPersisted,
            input_data: progressInputData,
          },
          `After allocated_programmes chunk ${insertChunkIdx}/${insertChunks} (${rowsPersisted}/${saveRowTotal} rows saved)`
        );
        dispatch(
          addSummary({
            ...preliminaryInputData,
            ...sumPayload,
          })
        );
      }

      const { data: historyData, error: completeErr } = await supabase
        .from("batch_jobs")
        .update({
          status: statusType.COMPLETED,
          total_records: mergedRows.length,
          processed_records: mergedRows.length,
          completed_at: new Date().toISOString(),
          input_data,
        })
        .eq("id", batchJobId)
        .select("*")
        .maybeSingle();

      if (completeErr || !historyData) {
        console.error("[Skills Plan] batch_jobs complete", completeErr);
        toast.error(completeErr?.message || messages.SERVER_ERROR);
        return;
      }

      const rowData = allocatedData.map((ele) => ({
        ...ele.data,
        uniqueId: ele.id,
      }));
      dispatch(addHistoryData(historyData));
      dispatch(addPrograms(rowData));
      const summaryPayload = {
        ...historyData.input_data,
        ...findSummary(rowData, historyData),
      };
      dispatch(addSummary(summaryPayload));
      await dispatch(trainingHistory(localData.company_id));

      toast.success(
        `Saved ${mergedRows.length} row(s) for ${formatedRecords.length} employee(s).`
      );
    } catch (e) {
      const msg = e?.message || String(e);
      console.error(
        "[Skills Plan] Generate Plan failed: unexpected error (outer catch)",
        {
          message: msg,
          name: e?.name,
          stack: e?.stack,
          batchJobId,
        },
        e
      );
      toast.error(
        `${messages.SOMETHING_WENT_WRONG} ${msg ? `(${msg})` : ""}`.trim()
      );
      if (batchJobId) await markBatchJobFailed(batchJobId);
    } finally {
      setGeneratePlanLoading(false);
      setIsGeneratingPlan(false);
    }
  };

  const handleCreatePlanClick = async () => {
    if (!localData?.company_id) return;

    setGeneratePlanLoading(true);
    let deferLoadingToGeneration = false;
    try {
      const { data, error } = await fetchAllTrainingFormsForGeneratePlan(
        supabase,
        localData.company_id,
        formStatusFilter,
      );

      if (error) {
        console.error("[Skills Plan] get_training_forms", error);
        toast.error(error.message || messages.SERVER_ERROR);
        return;
      }

      const trainingFormData = Array.isArray(data) ? data : [];
      if (trainingFormData.length === 0) {
        toast.error("No training forms for this company/filter.");
        return;
      }

      const fields = Array.isArray(setaTemplate) ? setaTemplate : [];
      if (fields.length === 0) {
        toast.error("SETA template is not loaded.");
        return;
      }

      const empFields =
        Array.isArray(employeeTemplate) && employeeTemplate.length > 0
          ? employeeTemplate
          : fields;

      const empTypesFromField = employmentTypesFromSetaFields(empFields);
      const employmentTypesForValidation =
        empTypesFromField.length > 0
          ? empTypesFromField
          : Array.isArray(employmentTypes)
            ? employmentTypes
            : [];

      const issues = collectPlanEmployeeDataIssues(trainingFormData, {
        setaTemplateFields: empFields,
        isWorkflowReq: Boolean(configData?.training_need_workflow),
        employmentTypes: employmentTypesForValidation,
        companyId: localData.company_id,
      });

      if (issues.length > 0) {
        const filterLabel =
          SKILLS_PLANNER_FORM_STATUS_FILTER_OPTIONS.find(
            (option) => option.value === formStatusFilter,
          )?.label ?? formStatusFilter;
        setEmployeeDataWarningDialog({
          show: true,
          issues,
          pendingTrainingFormData: trainingFormData,
          filterLabel,
        });
        return;
      }

      deferLoadingToGeneration = true;
      await runSkillsPlannerGeneratePlan(trainingFormData);
    } catch (e) {
      const msg = e?.message || String(e);
      console.error("[Skills Plan] Create Plan preflight failed", e);
      toast.error(
        `${messages.SOMETHING_WENT_WRONG} ${msg ? `(${msg})` : ""}`.trim(),
      );
    } finally {
      if (!deferLoadingToGeneration) {
        setGeneratePlanLoading(false);
      }
    }
  };

  const handleCloseEmployeeDataWarningDialog = () => {
    setEmployeeDataWarningDialog(EMPTY_EMPLOYEE_DATA_WARNING_DIALOG);
    setGeneratePlanLoading(false);
  };

  const handleConfirmProceedWithInvalidEmployeeData = async () => {
    const pendingTrainingFormData =
      employeeDataWarningDialog.pendingTrainingFormData;
    setEmployeeDataWarningDialog(EMPTY_EMPLOYEE_DATA_WARNING_DIALOG);
    if (!Array.isArray(pendingTrainingFormData) || pendingTrainingFormData.length === 0) {
      setGeneratePlanLoading(false);
      return;
    }
    await runSkillsPlannerGeneratePlan(pendingTrainingFormData);
  };

  const handleSubmit = async () => {
    const { count, error } = await supabase
      .from("company_user")
      .select("*", {
        count: "exact",
        head: true,
      })
      .eq("status", statuses.ACTIVE)
      .eq("company_id", localData.company_id)
      .not("data", "is", null);

    if (error) {
      toast.error(messages.SERVER_ERROR);
      return;
    }

    if (!count) {
      toast.error(messages.NO_DATA);
      return;
    }
    if (configData?.training_need_workflow && configData.select_training) {
      setIsLoading(true);
      setAllowCreateNew(false);
      dispatch(
        allocatePrograms({
          totalBudget: totalBudget,
          totalNumberOfEmployee: count,
          companyId: getLocalData()?.company_id,
          userId: getLocalData()?.id,
          approach: 'structured_with_training_selection',
          form_status_filter: formStatusFilter,
          approved_only:
            formStatusFilter === SKILLS_PLANNER_FORM_STATUS_FILTER.APPROVED,
        })
      ).then((res) => {
        if (res.payload?.error) {
          console.error("Error in allocatePrograms:", res.payload.error);
          toast.error(res.payload.error || messages.SOMETHING_WENT_WRONG);
          setIsLoading(false);
          setAllowCreateNew(true);
          return;
        }
        
        if (!res.payload?.data) {
          console.error("No data in response:", res.payload);
          toast.error(messages.SOMETHING_WENT_WRONG);
          setIsLoading(false);
          setAllowCreateNew(true);
          return;
        }

        const {
          id,
          processed_records,
          total_records,
          input_data,
          job_type,
          ...restData
        } = res.payload.data;
        
        if(restData.status == statusType.COMPLETED || restData.status == statusType.FAILED){
          setIsLoading(false);
          setAllowCreateNew(true);
          return;
        }
        dispatch(addHistoryTableId(id));
        dispatch(addHistoryData(res.payload.data));
        dispatch(addPrograms([]));
        if (restData.status != statusType.FAILED) {
          dispatch(
            addSummary({
              budgetUsed: 0,
            })
          );
        }
      }).catch((error) => {
        console.error("Error in allocatePrograms:", error);
        toast.error(error?.message || messages.SOMETHING_WENT_WRONG);
        setIsLoading(false);
        setAllowCreateNew(true);
      });
    } else if (!configData?.training_need_workflow) {
      if (
        !budget.allocateBasedOn ||
        !budget.totalBudget ||
        isNaN(budget.totalBudget)
      ) {
        toast.error(messages.FILL_ALL_FIELDS);
        return;
      }

      if (
        budget.allocateBasedOn !== allocationType.EQUAL &&
        100 != findTotal()
      ) {
        toast.error(messages.ALOT_ALL_WITHIN_BUDGET);
        return;
      }
      handleClose();

      setIsLoading(true);
      setAllowCreateNew(false);
      dispatch(
        allocatePrograms({
          ...budget,
          [budget.allocateBasedOn]: {
            ...(budget.allocateBasedOn === allocationType.GENDER &&
              genderBudget),
            ...(budget.allocateBasedOn === allocationType.RACE && raceBudget),
          },
          totalNumber:
            budget.allocateBasedOn === allocationType.GENDER
              ? statistics.gender
              : budget.allocateBasedOn === allocationType.RACE
              ? statistics.race
              : statistics.totalEmployee,
          companyId: getLocalData("sdfUser")?.company_id,
          userId: getLocalData("sdfUser")?.user_id,
          totalNumberOfEmployee: statistics.totalEmployee,
          approach: 'unstructured',
        })
      ).then(async (res) => {
        if (!res.payload.error) {
          const {
            id,
            processed_records,
            total_records,
            input_data,
            job_type,
            ...restData
          } = res.payload.data;

          const { allocateBasedOn, ...rest } = input_data;

          if(restData.status == statusType.COMPLETED || restData.status == statusType.FAILED){
            setIsLoading(false);
            setAllowCreateNew(true)
            return;
          }
          dispatch(addHistoryTableId(id));
          dispatch(addHistoryData(res.payload.data));
          dispatch(addPrograms([]));
          if (restData.status != statusType.FAILED) {
            let keys = {};
            Object.keys(rest[allocateBasedOn])?.forEach((ele) => {
              keys[ele] = 0;
            });
            dispatch(
              addSummary({
                budgetUsed: 0,
                budgetUsedBy: Object.keys(rest[allocateBasedOn]).length
                  ? keys
                  : null,
              })
            );
          }
        }
      });
    }
  };

  const [allocatedProgramsSubscriptions, setAllocatedProgramsSubscriptions] = useState(null);

  useEffect(() => {
    if (!historyTableId) return;

    const updateTrainingHistory = supabase
      .channel("trainingHistoryUpdates")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "batch_jobs",
          filter: `id=eq.${historyTableId}`,
        },
        (payload) => {
          const { id, processed_records, total_records, job_type, ...rest } =
            payload?.new;
          if (generateTrainingPageBatchJobTypes.includes(job_type)) {
            dispatch(addHistoryData(payload.new));
          }

          if (
            statusType.COMPLETED === rest.status ||
            statusType.FAILED === rest.status
          ) {
            setIsLoading(false);
            setAllowCreateNew(true);
          }
        }
      )
      .subscribe();

    const historySubTable = supabase
      .channel("subTable")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "allocated_programmes",
          filter: `batch_jobs_id=eq.${historyTableId}`,
        },
        (payload) => {
          if (isLoading) {
            setIsLoading(false);
          }
          if(history[0]?.status == statusType.COMPLETED || history[0]?.status == statusType.FAILED){
            setAllowCreateNew(true);
            return;
          }
          if (
            history[0]?.status.toLowerCase() != statusType.FAILED &&
            payload.new.data != null
          ) {
            const programsAllocated =
              store.getState().trainingProgramme.allocatedPrograms;
            dispatch(
              addPrograms([
                ...programsAllocated,
                { ...payload.new.data, uniqueId: payload.new.id },
              ])
            );

            const costField = setaTemplate?.find(f => f?.config?.isTrainingCostField)?.fieldName;
            let trainingCost = 0;
            if (costField) {
              trainingCost = !isNaN(payload.new.data[costField])
                ? Number(payload.new.data[costField])
                : 0;
            }

            const currentSummary = store.getState().trainingProgramme.summary;

            let oldBudgetUsedBy;
            if (currentSummary.budgetUsedBy) {
              const data = payload?.new?.data;
              oldBudgetUsedBy = JSON.parse(
                JSON.stringify(currentSummary.budgetUsedBy)
              );
              const key =
                data[
                  capitalizeFirstCharacter(budget?.allocateBasedOn)
                ]?.toLowerCase() || "other";
              if (costField) {
                trainingCost = !isNaN(data[costField])
                  ? Number(data[costField])
                  : 0;
              }
              oldBudgetUsedBy[key] += trainingCost;
            }
            dispatch(
              addSummary({
                ...currentSummary,
                budgetUsed: (currentSummary.budgetUsed || 0) + trainingCost,
                budgetUsedBy: oldBudgetUsedBy,
              })
            );
          }
        }
      )
      .subscribe();

    setAllocatedProgramsSubscriptions(historySubTable);

    return () => {
      supabase.removeChannel(updateTrainingHistory);
      supabase.removeChannel(historySubTable);
    };
  }, [historyTableId]);

  function findSummary(data, item) {
    const inputData =
      item?.input_data != null && typeof item.input_data === "object"
        ? item.input_data
        : {};
    const { allocateBasedOn, ...rest } = inputData;
    let keys = {};
    let totalBudgetUsed = 0;

    const costField = setaTemplate?.find(
      (f) => f?.config?.isTrainingCostField
    )?.fieldName;
    if (!costField) {
      return {
        budgetUsed: 0,
        budgetUsedBy: keys,
      };
    }

    const breakdown =
      allocateBasedOn && rest[allocateBasedOn] != null
        ? rest[allocateBasedOn]
        : null;
    const hasBreakdown =
      allocateBasedOn &&
      allocateBasedOn !== statusType.EQUAL &&
      breakdown &&
      typeof breakdown === "object" &&
      !Array.isArray(breakdown);
    if (hasBreakdown) {
      Object.keys(breakdown).forEach((ele) => {
        keys[ele.toLowerCase()] = 0;
      });
    }

    data.forEach((ele) => {
      const trainingCost = !isNaN(ele[costField])
        ? Number(ele[costField])
        : 0;
      totalBudgetUsed += trainingCost;
      if (Object.keys(keys).length > 0 && allocateBasedOn) {
        const dimKey =
          ele[capitalizeFirstCharacter(allocateBasedOn)]?.toLowerCase() ||
          "other";
        keys[dimKey] = (keys[dimKey] || 0) + trainingCost;
      }
    });

    return {
      budgetUsed: totalBudgetUsed,
      budgetUsedBy: keys,
    };
  }

  const handleLoadData = async (item) => {
    if (allocatedProgramsSubscriptions) {
      supabase.removeChannel(allocatedProgramsSubscriptions);
      setAllocatedProgramsSubscriptions(null);
    }

    setFormStatusFilter(
      resolveSkillsPlannerFormStatusFilter(item?.input_data),
    );
    setIsLoading(true);
    const { data, error } = await supabase
      .from("allocated_programmes")
      .select("data, id")
      .eq("batch_jobs_id", item.id);

    if (error) {
      toast.error(messages.SERVER_ERROR);
      setIsLoading(false);
      return;
    }
    const rowData = data.map((ele) => {
      return { ...ele.data, uniqueId: ele.id };
    });
    dispatch(addPrograms(rowData));
    if (
      item.status === statusType.PROCESSING &&
      item.job_type === jobType.TRAINING
    ) {
      dispatch(addHistoryTableId(item.id));
    }

    let summary = {
      ...item.input_data,
      ...findSummary(rowData, item),
    };
    dispatch(addSummary(summary));

    // Stop loading when fetch succeeded. Empty [] must not leave the grid stuck on
    // "Processing data..." (CDataGrid loading overlay).
    // Exception: edge generate_training_plan still PROCESSING with no rows yet — rows
    // arrive via realtime INSERT; keep loading until inserts or batch_jobs COMPLETED/FAILED.
    const awaitingStreamedEdgeRows =
      rowData.length === 0 &&
      item.status === statusType.PROCESSING &&
      item.job_type === jobType.TRAINING;

    if (!awaitingStreamedEdgeRows) {
      setIsLoading(false);
    }
  };

  const handleDeleteHistory = async (item) => {
    const { data: deleteHistoryTable, error: deleteHistoryTableError } =
      await supabase.from("batch_jobs").delete().eq("id", item.id);
    if (deleteHistoryTableError) {
      if (deleteHistoryTableError.code === "23503") {
        return toast.error(
          "Cannot delete this batch job because it has related records (e.g. employees, allocated programmes, or logs). Remove or unlink related data first."
        );
      }
      return toast.error(messages.SERVER_ERROR);
    }
    dispatch(addPrograms([]));
    dispatch(addSummary({}));

    toast.success(messages.DELETED_SUCCESSFULLY);
    setIsLoading(false)
    if(!allowCreateNew){
      setAllowCreateNew(true)
    }
    dispatch(deleteHistoryData(item));
  };

  const handleResume = async (item) => {
    const companyId = localData?.company_id;
    if (!companyId) {
      toast.error("Company context not found.");
      return;
    }
    setResumingId(item.id);
    try {
      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        "resume_stuck_batch_job",
        { p_batch_job_id: item.id }
      );
      if (rpcError) {
        toast.error(rpcError.message || "Failed to trigger resume.");
        setResumingId(null);
        return;
      }
      const result = typeof rpcResult === "string" ? JSON.parse(rpcResult) : rpcResult;
      if (!result?.success) {
        toast.error(result?.error || "Resume failed.");
        setResumingId(null);
        return;
      }
      toast.success(result?.message || "Resume triggered successfully.");
      dispatch(trainingHistory(companyId));
    } catch (err) {
      toast.error(err?.message || messages.SERVER_ERROR);
    } finally {
      setResumingId(null);
    }
  };

  const handleExport = async () => {
    if (!allocatedPrograms || !allocatedPrograms.length) {
      toast.error("No allocated programs to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const sheetsList = fileStructure?.sheets || [];
    const empSheetConfig =
      sheetsList.find((s) => s.sheetRole === "employeeImport") ||
      sheetsList.find((s) => s.sheetName === "Employee") ||
      sheetsList.find(
        (s) =>
          /^employee/i.test(s.sheetName?.trim() || "") ||
          (!s.fields?.some((f) => f?.config?.generatePlan) &&
            s.fields?.some((f) => f?.config?.employeeImport))
      );
    const wspSheetConfig =
      sheetsList.find((s) => s.sheetRole === "planExport") ||
      sheetsList.find((s) => s.sheetName === "WSP") ||
      sheetsList.find((s) => s.fields?.some((f) => f?.config?.generatePlan)) ||
      sheetsList[0];

    // Multi-tab export (e.g. merSETA or MICT with Employee workforce profile and WSP planned interventions)
    if (sheetsList.length > 1 && empSheetConfig && wspSheetConfig && empSheetConfig !== wspSheetConfig) {
      try {
        // Tab 1: Employee Sheet
        const empSheetTabName = empSheetConfig.sheetName || "Employee";
        const empOutputFields = (empSheetConfig?.fields || []).filter(
          (f) => f.config?.exportSheet && !f.config?.isDepartmentNameField && !f.config?.isEmailField
        );

        const { data: empUsers, error: empErr } = await supabase
          .from("company_user")
          .select("data, emp_id, first_name, last_name, gender, race, disability_status, employment_type")
          .eq("status", statuses.ACTIVE)
          .eq("company_id", localData.company_id)
          .eq("role", roleType.EMPLOYEE)
          .not("data", "is", null);

        if (!empErr && empUsers?.length) {
          const empExportData = empUsers.map((user) => {
            const d = user.data || {};
            const row = {};
            empOutputFields.forEach((field) => {
              const fn = field.fieldName;
              row[fn] = d[fn] !== undefined && d[fn] !== null && d[fn] !== "" ? d[fn] : "-";
            });
            return row;
          });
          const wsEmployee = XLSX.utils.json_to_sheet(empExportData);
          XLSX.utils.book_append_sheet(wb, wsEmployee, empSheetTabName.trim() || "Employee");
        }

        // Tab 2: WSP Sheet
        const wspSheetTabName = wspSheetConfig.sheetName || "WSP";
        const wspOutputFields = (wspSheetConfig?.fields || []).filter(
          (f) => f.config?.exportSheet && !f.config?.isDepartmentNameField && !f.config?.isEmailField
        );

        let skilledInfo;
        wspOutputFields.forEach((field) => {
          if (field.config?.isSkillInfoField) {
            skilledInfo = field.fieldName;
          }
        });

        const wspExportData = allocatedPrograms.map((record) => {
          const filteredEntry = {};
          wspOutputFields.forEach((fieldObj) => {
            const field = fieldObj.fieldName;
            const value = record.allocated_additional_field?.[field] ?? record[field];
            const isTrainingCostField = fieldObj.config?.isTrainingCostField;

            if (isTrainingCostField) {
              if (typeof value !== "number" || isNaN(value)) {
                filteredEntry[field] = "-";
              } else {
                filteredEntry[field] = value;
              }
            } else if (field === skilledInfo && Array.isArray(value)) {
              filteredEntry[field] = value.join(", ");
            } else if (Array.isArray(value)) {
              filteredEntry[field] = value.join(", ");
            } else {
              filteredEntry[field] =
                value !== undefined && value !== null && value !== "" ? value : "-";
            }
          });
          return filteredEntry;
        });

        const wsWsp = XLSX.utils.json_to_sheet(wspExportData);
        XLSX.utils.book_append_sheet(wb, wsWsp, wspSheetTabName.trim() || "WSP");

        const exportPrefix = (fileStructure?.name || "SETA_Plan")
          .replace(/[^a-zA-Z0-9_-]/g, "_")
          .substring(0, 30);
        XLSX.writeFile(wb, `${exportPrefix}_WSP_Submission-${Date.now()}.xlsx`);
        return;
      } catch (err) {
        console.error("[Skills Plan] multi-tab export failed, falling back to single tab", err);
      }
    }

    // Default single-tab export (FASSET, INSETA, etc.)
    let skilledInfo;
    const outputRequiredFields = setaTemplate
      ?.filter((field) =>
        configData?.training_need_workflow
          ? field?.config?.exportSheet
          : field?.config?.exportSheet && !field?.config?.isDepartmentNameField
      )
      .map((field) => {
        if (field.config.isSkillInfoField) {
          skilledInfo = field.fieldName;
        }
        return field.fieldName;
      });

    const filteredData = allocatedPrograms?.map((record) => {
      const filteredEntry = {};

      outputRequiredFields.forEach((field) => {
        const value = record.allocated_additional_field?.[field] ?? record[field];
        const isTrainingCostField = setaTemplate?.find(
          (f) => f.fieldName === field
        )?.config?.isTrainingCostField;

        if (isTrainingCostField) {
          if (typeof value !== "number" || isNaN(value)) {
            filteredEntry[field] = "-";
          } else {
            filteredEntry[field] = value;
          }
        } else if (field === skilledInfo && Array.isArray(value)) {
          filteredEntry[field] = value.join(", ");
        } else if (Array.isArray(value)) {
          filteredEntry[field] = value.join(", ");
        } else {
          filteredEntry[field] =
            value !== undefined && value !== null && value !== "" ? value : "-";
        }
      });

      return filteredEntry;
    });
    const ws = XLSX.utils.json_to_sheet(filteredData);

    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    XLSX.writeFile(wb, `Allocated Programs-${Date.now()}.xlsx`);
  };

  const handleClose = async () => {
    setDialogState({ id: null, show: false });
    setBudget({ ...budget, totalBudget: null, remainingBudget: 100 });
  };

  return (
    <div className="flex items-center justify-between flex-wrap h-[calc(100vh-68px)] relative">
      <div
        className={`h-full border-r border-solid border-grey-400 transition-all duration-500 ease-in-out overflow-hidden ${
          isAISidebarOpen
            ? "w-[600px] opacity-100"
            : "w-0 border-none opacity-0"
        }`}
      >
        <div className="grid grid-cols-1 grid-rows-[60px_auto] h-full">
          <div className="flex justify-between items-center py-1 border-b border-solid border-grey-300 px-3">
            <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-blue-500">
              Generated Training Plan History
            </p>

            <CButton onClick={() => setIsAISidebarOpen(false)} className="!p-0">
              <ToggleBarIcon className="w-[24px] h-[24px] !stroke-blue-500" />
            </CButton>
          </div>
          <div className="p-4">
            <div className="overflow-y-auto h-fit-content max-h-[calc(100vh-164px)] rounded-lg border border-solid border-blue-200">
              <table className="w-full border-collapse custom-table ">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Date
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Summary
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Status
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 px-2 py-2">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history?.map((item) => {
                    return (
                      <tr key={item.id}>
                        <td
                          className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-1 w-[170px] text-gray-500"
                        >
                          {moment(item.created_at).format("DD-MM-YYYY hh:mm A")}
                        </td>
                        <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-1 text-gray-500">
                          <p className="relative">
                            Total budget:
                            <span className="mx-1 text-blue-500 font-medium">
                              {formatZAR(item?.input_data?.totalBudget)}
                            </span>
                          </p>
                          <p>
                            Budget Used:
                            <span className="mx-1 text-blue-500 font-medium">
                              {formatZAR(item?.input_data?.usedBudget)}
                            </span>
                          </p>
                          {/* Allocation type hidden — still stored on input_data.allocateBasedOn for gender/race plans */}
                          {/* {item?.input_data?.allocateBasedOn && (
                            <p>
                              Allocation type:
                              <span className="mx-1 text-blue-500 font-medium capitalize">
                                {item?.input_data?.allocateBasedOn}
                              </span>
                            </p>
                          )} */}
                        </td>
                        <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-2 text-gray-500">
                          {item?.status === statusType.FAILED ? (
                            <div className="flex justify-center">
                              <span className="text-xs font-medium text-red-500">
                                Failed
                              </span>
                            </div>
                          ) : item?.status === statusType.COMPLETED ? (
                            <div className="flex justify-center">
                              <span className="text-xs font-medium text-blue-500">
                                100%
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                                <div
                                  className="h-2 rounded bg-blue-500"
                                  style={{
                                    width: `${
                                      item?.total_records
                                        ? Math.min(
                                            100,
                                            Math.round(
                                              (item?.processed_records /
                                                item?.total_records) *
                                                100
                                            )
                                          )
                                        : 0
                                    }%`,
                                    transition: "width 0.3s",
                                  }}
                                ></div>
                              </div>
                              <div className="flex justify-center">
                                <span className="text-xs text-blue-500">
                                  {item?.total_records
                                    ? `${Math.min(
                                        100,
                                        Math.round(
                                          (item?.processed_records /
                                            item?.total_records) *
                                            100
                                        )
                                      )}%`
                                    : item?.status === statusType.PROCESSING
                                      ? "…"
                                      : "0%"}
                                </span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm px-2 py-2 text-gray-500">
                          <div className="flex items-center gap-2">
                            <CButton
                              onClick={() => handleLoadData(item)}
                              className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 !h-[28px] !w-[28px] !p-0 !rounded-[4px] inline-flex items-center justify-center"
                            >
                              <ViewIcon className="w-4 h-4" />
                            </CButton>
                            {(item?.status === statusType.FAILED ||
                              (item?.status === statusType.PROCESSING &&
                                item?.total_records != null &&
                                item?.processed_records < item?.total_records)) && (
                              <Tooltip title="Resume">
                                <span>
                                  <CButton
                                    onClick={() => handleResume(item)}
                                    disabled={resumingId === item.id}
                                    className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 !h-[28px] !w-[28px] !p-0 !rounded-[4px] inline-flex items-center justify-center disabled:opacity-50"
                                  >
                                    <ReplayIcon className="w-4 h-4" />
                                  </CButton>
                                </span>
                              </Tooltip>
                            )}
                            <CButton
                              onClick={() => {
                                handleDeleteHistory(item);
                              }}
                              className="!min-w-0 bg-red-100 text-red-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
                            >
                              <DeleteIcon />
                            </CButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {dialogState.show && (
          <CDialog open={dialogState.show} onClose={handleClose}>
            <CButton onClick={handleClose} className="absolute right-0 top-0">
              <CloseIcon />
            </CButton>
            <div className="p-3">
              <div className="flex justify-between items-center">
                <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-grey-500">
                  Budget Allocation:
                </p>
              </div>
              <div className="custom-input-group">
                <div className="flex items-center">
                  <CTextField
                    name="totalBudget"
                    label="Total Budget:"
                    className="budget-inputs !my-3"
                    value={budget.totalBudget}
                    autoComplete="off"
                    type="number"
                    min={0}
                    onChange={(e) => {
                      if (Number(e.target.value) || e.target.value === "")
                        setBudget({
                          ...budget,
                          [e.target.name]: Number(e.target.value),
                        });
                    }}
                  />
                  <span className="text-blue-500 font-semibold mx-2">R</span>
                </div>
                <CRadioGroup
                  name="radioOption"
                  label="Select an Option:"
                  row
                  className="budget-inputs"
                  value={budget.allocateBasedOn}
                  onChange={(e) => {
                    let gender = {};
                    Object.keys(genderBudget).forEach((ele) => {
                      gender[ele] = null;
                    });
                    setGenderBudget(gender);

                    let race = {};
                    Object.keys(raceBudget).forEach((ele) => {
                      race[ele] = null;
                    });
                    setRaceBudget(race);
                    setBudget({
                      ...budget,
                      remainingBudget: 100,
                      allocateBasedOn: e.target.value,
                    });
                  }}
                  spacing={2}
                  options={[
                    { value: "equal", label: "Equal Allocation" },
                    { value: "race", label: "Race" },
                    { value: "gender", label: "Gender" },
                  ]}
                />
                {budget.allocateBasedOn === "equal" && (
                  <div className="flex items-center gap-1 flex-wrap mt-2">
                    <p className="text-xs leading-xs font-normal text-blue-500 bg-blue-100 rounded-2xl px-3 py-1">
                      Total Employees{" "}
                      <span className="font-semibold">
                        {statistics?.totalEmployee}
                      </span>
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-1 flex-wrap mt-2">
                  {statistics[budget.allocateBasedOn] &&
                    Object.keys(statistics[budget.allocateBasedOn])?.map(
                      (ele) => (
                        <p
                          key={ele}
                          className="text-xs leading-xs font-normal text-blue-500 bg-blue-100 rounded-2xl  px-3 py-1 capitalize"
                        >
                          {ele}{" "}
                          <span className="font-semibold">
                            {statistics[budget.allocateBasedOn][ele]}
                          </span>
                        </p>
                      )
                    )}
                </div>
                <div className="budget-inner-inputs">
                  <p className="text-sm leading-sm text-blue-500 font-medium mt-2 capitalize">
                    {budget.allocateBasedOn} Based Allocation:{" "}
                    {Number(budget?.remainingBudget)?.toFixed(2)}%
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {statistics[budget.allocateBasedOn] &&
                      Object.keys(statistics[budget.allocateBasedOn])?.map(
                        (ele) => (
                          <CTextField
                            name={ele}
                            label={ele}
                            value={
                              budget.allocateBasedOn === allocationType.GENDER
                                ? genderBudget[ele]
                                : raceBudget[ele]
                            }
                            onChange={(e) => handleBudgetChange(e)}
                            disabled={
                              !statistics[budget.allocateBasedOn][ele] && true
                            }
                            autoComplete="off"
                            className="!2xl:mt-2 !mt-2 capitalize !w-[130px]"
                            endIcon={<PercentIcon className="fill-blue-300" />}
                          />
                        )
                      )}
                  </div>
                </div>
                <div className="flex justify-center mt-3">
                  <CButton
                    className="chat-ai-outlined-btn !h-[40px] text-sm leading-sm"
                    onClick={handleSubmit}
                  >
                    <span className="ai-gradient-text">Generate Plan</span>
                  </CButton>
                </div>
              </div>
            </div>
          </CDialog>
        )}
      </div>

      <div
        className={`h-full transition-[width] duration-500 ease-in-out 2xl:px-4 px-3 ${
          isAISidebarOpen ? "w-[calc(100%-600px)]" : "w-full"
        }`}
      >
        <div className="bg-white border border-solid border-blue-200 border-t-0 border-b-0  overflow-hidden">
          <div className="flex items-center justify-between p-2">
            <div className="flex items-center gap-2">
            {!isAISidebarOpen && (
                <CButton
                  className="!p-0 mr-2"
                  onClick={() => setIsAISidebarOpen(true)}
                  type="button"
                >
                  <ToggleBarIcon className="w-[24px] h-[24px] !stroke-blue-500 rotate-180" />
                </CButton>
              )}
            <CTextField
              name="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              startIcon={<SearchIcon className="text-blue-300" />}
              placeholder={"Search"}
              className="!mt-0 w-full 2xl:max-w-[300px] max-w-[200px]"
            />
            </div>
              <div className="flex items-center gap-2">
            {configData?.training_need_workflow && (
              <CSelect
                name="formStatusFilter"
                value={formStatusFilter}
                onChange={(e) => setFormStatusFilter(e.target.value)}
                options={SKILLS_PLANNER_FORM_STATUS_FILTER_OPTIONS}
                formControlClass="shrink-0 !w-[140px]"
                className="!mt-0 !h-[40px] md:!h-[45px] 2xl:!h-[50px]"
              />
            )}
              {/* Edge Create Plan toolbar — uncomment to restore
              <CButton
                className={`gradient-btn ${allowCreateNew ? "" : "opacity-50 cursor-not-allowed"} ${
                  isAISidebarOpen ? "block" : "hidden"
                }`}
                disabled={!allowCreateNew}
                onClick={(e) => {
                  e.stopPropagation();
                  if (configData.training_need_workflow) {
                    handleSubmit();
                  } else {
                    setDialogState({ show: true });
                    setBudget({
                      totalBudget: null,
                      allocateBasedOn: "",
                      remainingBudget: 100,
                    });
                  }
                }}
              >
                Create Plan
              </CButton>
              */}
              {configData?.training_need_workflow && (
                <CButton
                  type="button"
                  className={`gradient-btn ${
                    generatePlanLoading || !localData?.company_id
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  } ${isAISidebarOpen ? "block" : "hidden"}`}
                  disabled={generatePlanLoading || !localData?.company_id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreatePlanClick();
                  }}
                >
                  {generatePlanLoading
                    ? isGeneratingPlan
                      ? "Creating…"
                      : "Checking…"
                    : "Create Plan"}
                </CButton>
              )}
              <CButton
                className=" text-blue-500 hover:text-white hover:bg-blue-500 border border-solid border-blue-500 rounded-lg p-0  inline-flex items-center gap-1 group disabled:opacity-50"
                onClick={handleExport}
                disabled={!allocatedPrograms?.length && true}
              >
                <DownloadIcon className="fill-blue-500 group-hover:fill-white" />
                WSP File
              </CButton>
              <CButton
                className={`gradient-btn ${isAISidebarOpen ? "hidden" : ""}`}
                onClick={() => setIsAISidebarOpen(true)}
              >
                Open AI panel
              </CButton>
            </div>
          </div>

          <div className="h-[calc(100vh-134px)] w-full relative gen-training-table">
            <CDataGrid
              searchQuery={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              rows={allocatedPrograms}
              columns={columns || []}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              loading={isLoading}
              idKey="uniqueId"
              columnVisibilityModel={{
                id: false,
              }}
              className="bg-white shadow-md rounded-lg"
            />

            <div className="absolute bottom-[6px] left-[12px] h-[70px] w-[calc(100%-384px)]">
              <div className="flex gap-x-2 items-center flex-wrap">
                <p className="text-gray-700  font-semibold text-sm leading-sm w-full">
                  Total Budget Used:
                  <span className="text-blue-500 font-semibold mx-1">
                    {formatZAR(summary.budgetUsed)}
                  </span>
                </p>
                {summary?.budgetUsedBy &&
                  Object?.keys(summary?.budgetUsedBy)?.map((ele) => {
                    return (
                      <p className="text-gray-500 text-sm leading-sm  my-1 capitalize">
                        {ele}:
                        <span className="text-blue-500 font-semibold mx-1">
                          {formatZAR(summary?.budgetUsedBy[ele])}
                        </span>
                      </p>
                    );
                  })}
              </div>

            </div>
          </div>
        </div>
      </div>

      {employeeDataWarningDialog.show && (
        <CDialog
          open={employeeDataWarningDialog.show}
          onClose={handleCloseEmployeeDataWarningDialog}
          className="bg-[#95AEBB80] !min-w-[500px]"
        >
          <div className="p-3">
            <p className="2xl:text-xl 2xl:leading-xl text-base leading-base text-grey-700 font-semibold mb-4 pr-[64px]">
              Some employee details need attention
            </p>
            <CButton
              className="!h-auto !p-0 text-blue-500 font-semibold !ml-auto !underline absolute right-[16px] top-[16px]"
              onClick={handleCloseEmployeeDataWarningDialog}
              disabled={generatePlanLoading && isGeneratingPlan}
            >
              <CloseIcon className="text-gray-700" />
            </CButton>
            {employeeDataWarningDialog.issues.length > 0 && (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  {employeeDataWarningDialog.issues.length} employee
                  {employeeDataWarningDialog.issues.length === 1 ? "" : "s"}{" "}
                  included in your{" "}
                  {employeeDataWarningDialog.filterLabel || "selected"} plan{" "}
                  {employeeDataWarningDialog.issues.length === 1 ? "has" : "have"}{" "}
                  missing or incorrect details.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  We recommend fixing these on the Employee List before creating
                  your plan. You can still continue with the current information
                  if you prefer.
                </p>
                <div className="max-h-[240px] overflow-y-auto mb-3 rounded border border-gray-200 bg-white/80 p-3">
                  <ul className="text-sm text-gray-700 list-disc pl-5 space-y-2">
                    {employeeDataWarningDialog.issues
                      .slice(0, EMPLOYEE_DATA_WARNING_LIST_LIMIT)
                      .map((entry) => {
                        const label = [entry.empId, entry.name]
                          .filter(Boolean)
                          .join(" — ");
                        const issueLines = entry.issues
                          .map(formatEmployeeDataIssueText)
                          .filter(Boolean)
                          .join("; ");
                        return (
                          <li key={entry.id || entry.empId}>
                            <span className="font-medium">
                              {label || "Employee"}
                            </span>
                            {issueLines ? `: ${issueLines}` : null}
                          </li>
                        );
                      })}
                  </ul>
                </div>
                {employeeDataWarningDialog.issues.length >
                  EMPLOYEE_DATA_WARNING_LIST_LIMIT && (
                  <p className="text-sm text-gray-500 mb-4">
                    Showing first {EMPLOYEE_DATA_WARNING_LIST_LIMIT} of{" "}
                    {employeeDataWarningDialog.issues.length} employees.
                  </p>
                )}
              </>
            )}
            <div className="flex justify-center mt-8 gap-3">
              <CButton
                onClick={handleConfirmProceedWithInvalidEmployeeData}
                disabled={generatePlanLoading && isGeneratingPlan}
                className="bg-blue-500 hover:bg-blue-700 min-w-[80px] text-white border border-solid border-blue-500 hover:border-blue-700"
              >
                {generatePlanLoading && isGeneratingPlan
                  ? "Creating plan..."
                  : "Create plan anyway"}
              </CButton>
              <CButton
                onClick={handleCloseEmployeeDataWarningDialog}
                disabled={generatePlanLoading && isGeneratingPlan}
                className="bg-transparent hover:bg-blue-500 border border-solid border-blue-500 hover:border-blue-500 min-w-[80px] text-blue-500 hover:text-white"
              >
                Go back
              </CButton>
            </div>
          </div>
        </CDialog>
      )}
    </div>
  );
};

export default GenerateTraining;
